import styled from '@emotion/styled'
import {
  forwardRef,
  type ComponentPropsWithRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ForwardedRef,
  type ReactElement,
} from 'react'

export type ButtonVariant = 'primary' | 'solid' | 'ghost' | 'soft'
export type ButtonSize = 'sm' | 'md' | 'lg'

const SIZE_STYLES: Record<ButtonSize, { padding: string; fontSize: string }> = {
  sm: { padding: '6px 10px', fontSize: '12px' },
  md: { padding: '10px 16px', fontSize: '14px' },
  lg: { padding: '14px 22px', fontSize: '15px' },
}

const VARIANT_STYLES: Record<ButtonVariant, { bg: string; fg: string; border: string }> = {
  primary: { bg: 'var(--c-accent)', fg: 'var(--c-accent-fg)', border: 'transparent' },
  solid: { bg: 'var(--c-fg)', fg: 'var(--c-bg)', border: 'transparent' },
  ghost: { bg: 'transparent', fg: 'var(--c-fg)', border: 'var(--c-border-strong)' },
  soft: { bg: 'var(--c-accent-soft)', fg: 'var(--c-accent)', border: 'transparent' },
}

const StyledButton = styled.button<{ variant: ButtonVariant; size: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  border-radius: var(--r-md);
  font-family: var(--font-sans);
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition:
    transform var(--d-fast) var(--ease-standard),
    background var(--d-fast) var(--ease-standard),
    border-color var(--d-fast) var(--ease-standard);
  white-space: nowrap;

  padding: ${({ size }) => SIZE_STYLES[size].padding};
  font-size: ${({ size }) => SIZE_STYLES[size].fontSize};
  background: ${({ variant }) => VARIANT_STYLES[variant].bg};
  color: ${({ variant }) => VARIANT_STYLES[variant].fg};
  border-color: ${({ variant }) => VARIANT_STYLES[variant].border};

  &:hover:not(:disabled):not([aria-disabled='true']) {
    transform: translateY(-1px);
  }
  &:active:not(:disabled):not([aria-disabled='true']) {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid var(--c-accent);
    outline-offset: 2px;
  }
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`

// Own props (the design-system knobs) merged with the polymorphic
// element's own props. Using `Omit` so `as` is the only source of
// truth for the rendered element and never collides with consumer
// attributes (e.g. `type` on `<button>` vs `<a>`).
export type ButtonOwnProps = {
  variant?: ButtonVariant
  size?: ButtonSize
}

export type ButtonProps<T extends ElementType = 'button'> = ButtonOwnProps & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof ButtonOwnProps | 'as'>

// Polymorphic + ref-forwarding Button. When `as` is provided, both the
// rendered element AND the ref type narrow to that element — so
// `<Button as="a" href="/x" ref={anchorRef}>` is type-safe end-to-end.
// Casts are confined to the wrapper; consumers see a clean generic.
type ButtonComponent = <T extends ElementType = 'button'>(
  props: ButtonProps<T> & { ref?: ComponentPropsWithRef<T>['ref'] },
) => ReactElement | null

export const Button = forwardRef(function Button<T extends ElementType = 'button'>(
  { variant = 'solid', size = 'md', as, ...rest }: ButtonProps<T>,
  ref: ForwardedRef<Element>,
) {
  return (
    <StyledButton
      as={as as ElementType | undefined}
      variant={variant}
      size={size}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      {...(rest as Record<string, unknown>)}
    />
  )
}) as ButtonComponent
